<template>
  <div class="wx-launch-container">
    <!-- 在小程序环境中显示普通按钮 -->
    <template v-if="isMiniProgram">
      <div class="launch-container" @click="handleMiniProgramClick">
        <img 
          class="launch-image" 
          :style="{
            width: imageWidth,
            height: imageHeight,
            marginBottom: imageMargin
          }"
          :src="imageUrl" 
          :alt="title" 
        />
        <div 
          v-if="title"
          class="launch-text"
          :style="{
            fontSize: fontSize,
            marginTop: textMargin
          }"
        >
          {{title}}
        </div>
      </div>
    </template>
    <!-- 在普通微信环境中显示开放标签 -->
    <template v-else>
      <!-- 简化微信开放标签，移除子内容 -->
      <wx-open-launch-weapp
        :id="elementId"
        :username="username"
        :path="path"
        @launch="handleLaunch"
        @error="handleError"
      >
        <!-- 空内容，将在mounted阶段通过DOM操作添加 -->
      </wx-open-launch-weapp>
      <!-- 调试信息 -->
      <div v-if="showDebugInfo" class="debug-info">
        <p>环境: {{ envInfo }}</p>
        <p>组件ID: {{ elementId }}</p>
        <p>配置状态: {{ configStatus }}</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeMount, nextTick, getCurrentInstance } from 'vue';
// 导入 Pinia store
import { useAuthStore } from '@/auth/store/authStore';

// -- Props Definition --
const props = defineProps({
  username: {
    type: String,
    required: true,
    default: 'gh_9318f8f3b6ea'
  },
  path: {
    type: String,
    required: true,
    default: 'pages/newSign/newSign'
  },
  imageUrl: {
    type: String,
    required: true
  },
  title: {
    type: String,
    default: ''
  },
  imageWidth: {
    type: String,
    default: '70%'
  },
  imageHeight: {
    type: String,
    default: 'auto'
  },
  imageMargin: {
    type: String,
    default: '0.2rem'
  },
  fontSize: {
    type: String,
    default: '0.7rem'
  },
  textMargin: {
    type: String,
    default: '0.2rem'
  },
  debug: {
    type: Boolean,
    default: false
  }
});

// -- Emits Definition --
const emit = defineEmits(['launch-success', 'launch-error']);

// -- Reactive State --
const elementId = ref(`launch-btn-${Date.now()}`);
const isMiniProgram = ref(false);
const isWechat = ref(false);
const configStatus = ref('未初始化');
const showDebugInfo = ref(process.env.NODE_ENV === 'development' || props.debug);

// Access auth store
const authStore = useAuthStore();

// Access global properties like $toast (if available)
const instance = getCurrentInstance();
const $toast = instance?.appContext.config.globalProperties.$toast;

// -- Computed Properties --
const envInfo = computed(() => `小程序: ${isMiniProgram.value}, 微信: ${isWechat.value}`);

// -- Methods / Functions --
const checkEnvironment = () => {
  const ua = navigator.userAgent.toLowerCase();
  isWechat.value = /micromessenger/.test(ua);

  // Check if wx is available before trying to use it
  if (typeof window.wx === 'undefined' || typeof window.wx.miniProgram === 'undefined') {
    console.warn('[CallWxMiniProgram] wx or wx.miniProgram is not available. Cannot check environment.');
    configStatus.value = '环境检查失败 (wx 不可用)';
    // Decide how to handle this, maybe assume not miniprogram?
    isMiniProgram.value = false;
    // If in WeChat but wx unavailable, perhaps try config later?
    return;
  }

  window.wx.miniProgram.getEnv(res => {
    isMiniProgram.value = res.miniprogram;
    configStatus.value = '环境检查完成';

    if (!isMiniProgram.value && isWechat.value) {
      initWxConfig();
    }
  });
};

const initWxConfig = async () => {
  try {
    configStatus.value = '配置初始化中';
    const url = encodeURIComponent(location.href.split('#')[0]);

    // 使用 authStore 获取 token
    const token = authStore.token || localStorage.getItem('token');

    if (!token) {
      // No longer throwing an error, just log and exit config
      console.warn('[CallWxMiniProgram] 未找到授权令牌，无法配置 JSSDK。');
      configStatus.value = '配置失败 (无 Token)';
      return; // Exit the function if no token
    }

    // 使用 fetch 请求签名
    const response = await fetch(`/newapi/wechat-jsapi/get-signuature?url=${url}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    console.log('[CallWxMiniProgram] 签名获取结果:', response.ok);
    if (!response.ok) {
      // Throw error only if response failed, not because of no token
      throw new Error(`签名获取失败: ${response.status}`);
    }
    const config = await response.json();

    // Check if wx is available before calling config
    if (typeof window.wx === 'undefined') {
        console.error('[CallWxMiniProgram] wx 对象在尝试配置时不可用。');
        configStatus.value = '配置失败 (wx 不可用)';
        return;
    }

    window.wx.config({
      debug: props.debug,
      appId: config.appId,
      timestamp: config.timestamp,
      nonceStr: config.nonceStr,
      signature: config.signature,
      jsApiList: [
        'onMenuShareTimeline',
        'onMenuShareAppMessage',
        'checkJsApi',
        'scanQRCode',
        'chooseImage',
        'previewImage',
        'wx-open-launch-weapp' // Keep this API
      ],
      openTagList: ['wx-open-launch-weapp']
    });

    window.wx.ready(() => {
      console.log('[CallWxMiniProgram] 微信配置就绪');
      configStatus.value = '配置就绪';
    });

    window.wx.error((err) => {
      console.error('[CallWxMiniProgram] 微信配置错误:', err);
      configStatus.value = '配置错误: ' + err.errMsg;
    });
  } catch (error) {
    console.error('[CallWxMiniProgram] 初始化微信配置失败:', error);
    configStatus.value = '初始化失败: ' + error.message;
  }
};

const setupWxOpenLaunchWeapp = () => {
  const openTag = document.getElementById(elementId.value);
  if (openTag) {
    const script = document.createElement('script');
    script.type = 'text/wxtag-template';
    // Use props directly
    script.innerHTML = `
      <div class="launch-container">
        <img 
          class="launch-image" 
          style="width: ${props.imageWidth}; height: ${props.imageHeight}; margin-bottom: ${props.imageMargin};"
          src="${props.imageUrl}" 
          alt="${props.title || ''}" 
        />
        ${props.title ? `
        <div 
          class="launch-text"
          style="font-size: ${props.fontSize}; margin-top: ${props.textMargin};"
        >
          ${props.title}
        </div>` : ''}
      </div>
    `;
    openTag.appendChild(script);
    console.log('[CallWxMiniProgram] 动态添加微信开放标签模板完成');

    setTimeout(() => {
      if (openTag.children.length === 0 || !openTag.querySelector('script[type="text/wxtag-template"]')) {
        console.warn('[CallWxMiniProgram] 微信开放标签模板可能未正确添加，请检查');
      }
    }, 300);
  } else {
    console.error('[CallWxMiniProgram] 找不到开放标签元素，ID:', elementId.value);
  }
};

const handleMiniProgramClick = () => {
  if (!isMiniProgram.value) return;

  if (typeof window.wx === 'undefined' || typeof window.wx.miniProgram === 'undefined') {
    console.error('[CallWxMiniProgram] wx or wx.miniProgram is not available for navigation.');
    alert('无法跳转，请稍后重试或在微信中打开。');
    return;
  }

  window.wx.miniProgram.navigateTo({
    url: `/${props.path}` // Use props.path
  }).then(() => {
    console.log('[CallWxMiniProgram] 小程序跳转成功');
    emit('launch-success');
  }).catch(error => {
    console.error('[CallWxMiniProgram] 小程序跳转失败:', error);
    emit('launch-error', { detail: error.message || '跳转失败' });
    // Use $toast if available, otherwise fallback to alert
    if ($toast) {
        $toast('跳转失败，请稍后重试');
    } else {
        alert('跳转失败，请稍后重试');
    }
  });
};

const handleLaunch = (e) => {
  console.log('[CallWxMiniProgram] 开放标签 launch:', e);
  emit('launch-success', e);
};

const handleError = (e) => {
  console.error('[CallWxMiniProgram] 开放标签 error:', e);
  emit('launch-error', e);
};

// -- Lifecycle Hooks --
// Equivalent of created: Run checkEnvironment directly
onBeforeMount(() => {
  checkEnvironment();
});

// Equivalent of mounted
onMounted(() => {
  if (!isMiniProgram.value) {
    nextTick(() => {
      setupWxOpenLaunchWeapp();
    });
  }
});

</script>

<style scoped>
.wx-launch-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.launch-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.launch-image {
  object-fit: contain;
}

.launch-text {
  color: #3a3a3a;
  text-align: center;
}

:deep(wx-open-launch-weapp) {
  display: block;
  width: 100%;
  height: 100%;
}

/* 调试信息样式 */
.debug-info {
  position: absolute;
  bottom: -20px;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 10px;
  padding: 2px;
  z-index: 100;
  display: none;
}

/* 在开发环境下显示调试信息 */
.wx-launch-container:hover .debug-info {
  display: block;
}
</style>
